import { Link } from "@tanstack/react-router";

import { WithcoWordmark } from "@/web/assets";
import {
  AnalyzeLeaseButton,
  AnalyzeLeaseMenuItem,
} from "@/web/components/styled/AnalyzeLeaseButton";
import {
  AppNavbar,
  PRIMARY_NAV_LINKS,
  type NavbarPosition,
} from "@/web/components/styled/AppNavbar";
import { DropdownMenuItem } from "@/web/components/primitives/dropdown-menu";
import { MARKETING_SITE_URL } from "@/web/lib/siteUrls";
import { cn } from "@/web/lib/styling";
import { NominateButton } from "@/web/routes/-nomination/NominateButton";
import { WithUsCta } from "@/web/routes/-marketing/WithUsCta";
import { type NavTheme, useSectionNavTheme } from "@/web/routes/-marketing/useSectionNavTheme";
import { AwardIcon } from "lucide-react";

/** Marks the marketing nav in the DOM so the scroll hook can measure its height. */
const NAV_SELECTOR = "[data-marketing-nav]";

/**
 * Per-theme styling for the landing-page navbar. The nav adopts the theme of
 * whichever sticky section currently sits beneath it (see `useSectionNavTheme`).
 * The nav background stays transparent throughout; only the logo and nav-link
 * colors swap to stay legible against the hero, white, red, and blue sections
 * in turn. `content` is the logo/link color for each section.
 */

const NAV_THEMES: Record<NavTheme, { content: string; contentMuted: string }> = {
  transparent: {
    content: "text-withus-yellow",
    contentMuted: "text-withus-white/70 hover:text-withus-white",
  },
  white: {
    content: "text-withus-blue",
    contentMuted: "text-foreground/60 hover:text-foreground",
  },
  red: {
    content: "text-withus-white",
    contentMuted: "text-withus-white/70 hover:text-withus-white",
  },
  blue: {
    content: "text-withus-white",
    contentMuted: "text-withus-white/70 hover:text-withus-white",
  },
};

/**
 * Landing-page navbar. Sits transparent over the hero, then tracks the sticky
 * section currently under it — fading its background and logo/text color to
 * stay legible against white, red, and blue sections in turn. The CTA and auth
 * buttons keep their fixed styling.
 *
 * Pages without the landing-page sections (e.g. About Us) pass `solid` to pin
 * the nav to its white treatment from the top.
 */
type MarketingNavbarProps = {
  solid?: boolean;
  position?: NavbarPosition;
  className?: string;
};

export function MarketingNavbar({
  solid = false,
  position = "fixed",
  className,
}: MarketingNavbarProps) {
  const sectionTheme = useSectionNavTheme(NAV_SELECTOR);
  const theme = solid ? "white" : sectionTheme;
  const t = NAV_THEMES[theme];
  return (
    <AppNavbar.Provider
      position={position}
      navLinks={PRIMARY_NAV_LINKS}
      mobileMenuClassName={t.content}
      mobileMenuExtras={
        <>
          <DropdownMenuItem asChild>
            <a href={MARKETING_SITE_URL} className="w-full cursor-pointer">
              Are you WITH.US?
            </a>
          </DropdownMenuItem>
          <AnalyzeLeaseMenuItem />
        </>
      }
    >
      <AppNavbar.Frame
        data-marketing-nav
        // The right-hand CTA row is wider than a third of the nav at lg, so size
        // the outer columns to their content rather than the Frame's equal thirds.
        className={cn(
          "grid-cols-[auto_minmax(0,1fr)_auto] gap-6",
          solid ? "bg-background" : "bg-transparent",
          className,
        )}
      >
        <AppNavbar.Left>
          <MarketingLogo contentClass={t.content} />
        </AppNavbar.Left>
        <AppNavbar.Center>
          <AppNavbar.NavLinks
            className="gap-6"
            getLinkClassName={(active) =>
              cn(
                "font-withus-sans text-base transition-colors",
                active ? cn("font-medium", t.content) : cn("font-medium", t.contentMuted),
              )
            }
          />
        </AppNavbar.Center>
        <AppNavbar.Right>
          {/* Matched to the md buttons beside it (the footer sticker is shorter). */}
          <WithUsCta className="inline-flex h-12 items-center" />
          <NominateButton size="md" icon={AwardIcon} color="blue" label="Nominate a business" />
          <AnalyzeLeaseButton size="md" color="blue" label="Analyze a lease" />
          <AppNavbar.AuthActions
            signInButtonProps={{
              variant: "solid",
              color: "blue",
              size: "md",
              label: "Sign in",
            }}
          />
        </AppNavbar.Right>
      </AppNavbar.Frame>
    </AppNavbar.Provider>
  );
}

/**
 * The wordmark/mark, colored to contrast with the current nav theme (yellow over
 * the hero, blue over white, white over red/blue).
 */
function MarketingLogo({ contentClass }: { contentClass: string }) {
  return (
    <Link to="/" className="shrink-0">
      <WithcoWordmark
        aria-hidden
        className={cn("block h-7 w-auto shrink-0 transition-colors md:hidden", contentClass)}
      />
      <WithcoWordmark
        aria-hidden
        className={cn("hidden h-8 w-auto shrink-0 transition-colors md:block", contentClass)}
      />
    </Link>
  );
}
